import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import Layout from "../components/Layout";
import SEO from "../components/SEO";
import { api, API, fmtMoney } from "../lib/api";

export default function Registry() {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    api.get("/registry")
      .then((r) => setData(r.data))
      .catch(() => setErr(t("registry.error")));
  }, [t]);

  const copy = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(t("registry.copied", { label }));
  };

  if (err) return <Layout><div className="max-w-3xl mx-auto px-6 py-24 text-ink2 font-mono text-sm">{err}</div></Layout>;
  if (!data) return <Layout><div className="max-w-3xl mx-auto px-6 py-24 text-ink2 font-mono text-sm">{t("registry.loading")}</div></Layout>;

  return (
    <Layout>
      <SEO
        title="Public B3 ISIN Registry — UCS Carbon Credits"
        description="Public registry of BMV UCS carbon credits with B3 ISIN codes. Verify unit balances and issuers directly at B3. Downloadable signed PDF for compliance teams."
        path="/registry"
      />
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pt-14 pb-8">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="label-tag mb-4">{t("registry.tag")}</div>
            <h1 className="font-serif text-5xl tracking-tight">{t("registry.title")}</h1>
            <p className="text-ink2 mt-3 max-w-2xl text-sm">{t("registry.sub")}</p>
          </div>
          <a
            href={`${API}/registry/export.pdf`}
            target="_blank"
            rel="noreferrer"
            onClick={() => toast.success(t("registry.generating"))}
            data-testid="download-registry-pdf"
            className="btn-primary !text-xs !px-5 !py-3 whitespace-nowrap">
            {t("registry.download")}
          </a>
        </div>
      </section>

      {/* Issuer + lookup helper */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-8 grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="lg:col-span-7 border border-ink p-6 bg-surface" data-testid="registry-issuer">
          <div className="label-tag mb-2">{t("registry.issuer")}</div>
          <div className="font-serif text-2xl">{data.issuer?.legal_name}</div>
          <div className="flex items-center gap-3 mt-3">
            <span className="label-tag">{t("registry.cnpj")}</span>
            <span className="font-mono text-sm" data-testid="registry-cnpj">{data.issuer?.cnpj}</span>
            <button
              onClick={() => copy(data.issuer?.cnpj, t("registry.cnpj"))}
              data-testid="copy-cnpj"
              className="text-[10px] font-mono uppercase tracking-wider underline underline-offset-4 text-ink2 hover:text-ink">
              {t("registry.copy")}
            </button>
          </div>
          <p className="text-xs text-ink2 mt-4 leading-relaxed">{t("registry.issuer_blurb")}</p>
        </div>

        <div className="lg:col-span-5 border border-line p-6 bg-bg">
          <div className="label-tag mb-2">{t("registry.how")}</div>
          <ol className="text-xs text-ink2 space-y-2 list-decimal pl-4 leading-relaxed">
            <li>{t("registry.step1_a")}<span className="font-mono text-ink">ISIN</span>{t("registry.step1_b")}</li>
            <li>{t("registry.step2")}</li>
            <li>{t("registry.step3_a")}<span className="font-mono text-ink">{t("registry.cnpj")} {data.issuer?.cnpj}</span>{t("registry.step3_b")}</li>
            <li>{t("registry.step4")}</li>
          </ol>
          <a
            href={data.lookup?.b3_url}
            target="_blank"
            rel="noreferrer"
            data-testid="b3-portal-link"
            className="btn-primary !text-xs mt-5 inline-block !px-5 !py-2">
            {t("registry.open_portal")}
          </a>
        </div>
      </section>

      {/* ISIN list */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-24">
        <div className="border border-line bg-surface overflow-x-auto">
          <table className="w-full text-sm" data-testid="registry-table">
            <thead>
              <tr className="border-b border-line text-left">
                <Th>{t("registry.th_isin")}</Th>
                <Th>{t("registry.th_project")}</Th>
                <Th>{t("registry.th_region")}</Th>
                <Th>{t("registry.th_bmv")}</Th>
                <Th className="text-right">{t("registry.th_tons")}</Th>
                <Th className="text-right">{t("registry.th_spot")}</Th>
                <Th></Th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((p) => (
                <tr key={p.b3_isin} className="border-b border-line/60 align-top hover:bg-bg/40" data-testid={`isin-row-${p.b3_isin}`}>
                  <Td>
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-sm">{p.b3_isin}</span>
                      <button
                        onClick={() => copy(p.b3_isin, "ISIN")}
                        data-testid={`copy-isin-${p.b3_isin}`}
                        className="text-[10px] font-mono uppercase underline underline-offset-4 text-ink2 hover:text-ink">
                        {t("registry.copy")}
                      </button>
                    </div>
                  </Td>
                  <Td>
                    <Link to={`/project/${p.id}`} className="font-serif underline-offset-4 hover:underline">
                      {p.name}
                    </Link>
                    <div className="text-xs text-ink2 mt-1">{p.producer}</div>
                  </Td>
                  <Td className="text-xs text-ink2">
                    <div>{p.region}</div>
                    <div className="mono-num">{p.vintage}</div>
                  </Td>
                  <Td className="text-xs">
                    <div className="font-mono">{p.bmv_unit_id}</div>
                    <div className="font-mono text-ink2 mt-1">{p.cpr_number}</div>
                  </Td>
                  <Td className="mono-num text-right">{Number(p.tons_available).toLocaleString()}</Td>
                  <Td className="mono-num text-right">{fmtMoney(p.price_usd_per_ton, "USD")}</Td>
                  <Td>
                    <a
                      href={data.lookup?.b3_url}
                      target="_blank"
                      rel="noreferrer"
                      data-testid={`verify-on-b3-${p.b3_isin}`}
                      className="text-[10px] font-mono uppercase tracking-wider underline underline-offset-4 text-ink2 hover:text-ink whitespace-nowrap">
                      {t("registry.verify_link")}
                    </a>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <p className="text-[11px] font-mono text-ink2 mt-6 leading-relaxed max-w-3xl">
          {t("registry.footnote")}
        </p>
      </section>
    </Layout>
  );
}

function Th({ children, className = "" }) {
  return <th className={`px-4 py-3 text-[11px] font-sans tracking-[0.2em] uppercase text-ink2 ${className}`}>{children}</th>;
}
function Td({ children, className = "" }) {
  return <td className={`px-4 py-3 ${className}`}>{children}</td>;
}
